/**
 * ADR-176 — Edit Amendment: revise a PENDING contract amendment in place,
 * before it takes effect. Admin-only (ContractAmendmentRevision on top of
 * ContractAmendment); the server re-checks every rule.
 *
 * What it can change: the effective date (future only), the NEW values of
 * the terms the amendment already names, signed-on date, the amendment's
 * reason and its Addendum reference. What it cannot: which terms are
 * amended — that is a different amendment (cancel this one, record the new
 * one). A Full Name change recorded on several contracts is revised on all
 * of them together.
 *
 * Mounted from two places: Edit on the contract's "What happens next" list
 * (Contracts tab) and the pencil on a Pending amendment row (Employment
 * Episodes tab).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { employmentApi } from '../../../services/api';
import { useAlert } from '../../../contexts/AlertContext';
import { employmentEventAnnotation } from '../../../constants/employmentEventAnnotations';
import { ContractTypeField, ContractCompanyField, EngagementModelField } from './ContractTermsFields';
import { initialRevisionStateOf, revisionBodyOf, trimmed } from '../../../constants/amendmentRevision';

dayjs.extend(utc);

const FIELD_LABELS = {
  engagementModelId: 'New Engagement Model',
  contractCompanyId: 'New Contract Company',
  contractTypeId: 'New Contract Type',
  fullName: 'New Full Name',
  termEndDate: 'New Contract End Date'
};

const pickerValueOf = (ymd) => (ymd ? dayjs(ymd) : null);
const DateField = ({ label, ymd, onChange, minDate, helperText }) => (
  <DatePicker
    label={label}
    value={pickerValueOf(ymd)}
    minDate={minDate}
    onChange={(nextValue) => {
      if (nextValue && typeof nextValue.isValid === 'function' && nextValue.isValid()) {
        onChange(nextValue.format('YYYY-MM-DD'));
      } else if (nextValue === null) {
        onChange(null);
      }
    }}
    format="DD/MM/YYYY"
    slotProps={{ textField: { fullWidth: true, helperText } }}
  />
);

const EditAmendmentDialog = ({ open, onClose, episodeId, amendmentEvent, warnClusterId = null, onRevised }) => {
  const initial = useMemo(() => initialRevisionStateOf(amendmentEvent), [amendmentEvent]);
  const [current, setCurrent] = useState(initial);
  const [revisionReason, setRevisionReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showAlert } = useAlert();

  useEffect(() => {
    setCurrent(initial);
    setRevisionReason('');
  }, [initial]);

  const payload = amendmentEvent?.payload || {};
  const number = payload.amendmentNumber;
  const groupSize = Number(payload.groupSize) || 0;
  const tomorrowYmd = dayjs().add(1, 'day').format('YYYY-MM-DD');

  const body = revisionBodyOf(initial, current, revisionReason);
  const hasChange = Object.keys(body).some((key) => key !== 'revisionReason');
  const valuesFilled = Object.values(current.changes).every((value) => Boolean(trimmed(value)));
  const dateValid = Boolean(current.effectiveYmd) && current.effectiveYmd >= tomorrowYmd;
  const canSubmit = hasChange && valuesFilled && dateValid
    && Boolean(trimmed(current.reason)) && Boolean(trimmed(revisionReason));

  const setChange = (field, value) =>
    setCurrent((state) => ({ ...state, changes: { ...state.changes, [field]: value } }));

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    onClose();
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const result = await employmentApi.reviseContractAmendment(episodeId, amendmentEvent.id, body);
      const others = Array.isArray(result?.alsoRevised) ? result.alsoRevised.length : 0;
      const also = others > 0 ? ` (and on ${others} other contract${others === 1 ? '' : 's'})` : '';
      showAlert(`Amendment${number ? ` #${number}` : ''} updated${also}`, 'success');
      onRevised();
    } catch (error) {
      showAlert(error.message || 'Failed to update the amendment', 'error', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderValueField = (field) => {
    const value = current.changes[field] ?? '';
    const label = FIELD_LABELS[field] || field;
    if (field === 'engagementModelId') {
      return <EngagementModelField key={field} value={value} onChange={(v) => setChange(field, v)} label={label} required />;
    }
    if (field === 'contractCompanyId') {
      return (
        <ContractCompanyField
          key={field}
          value={value}
          onChange={(v) => setChange(field, v)}
          label={label}
          warnClusterId={warnClusterId}
        />
      );
    }
    if (field === 'contractTypeId') {
      return <ContractTypeField key={field} value={value} onChange={(v) => setChange(field, v)} label={label} />;
    }
    if (field === 'termEndDate') {
      return <DateField key={field} label={label} ymd={value || null} onChange={(v) => setChange(field, v)} />;
    }
    return (
      <TextField
        key={field}
        label={label}
        value={value}
        onChange={(e) => setChange(field, e.target.value)}
        required
        fullWidth
      />
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{`Edit Amendment${number ? ` #${number}` : ''}`}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
        <Typography sx={{ fontSize: 14, color: '#64748B' }}>
          {amendmentEvent ? employmentEventAnnotation(amendmentEvent) : ''}
        </Typography>
        {groupSize > 1 && (
          <Typography sx={{ fontSize: 13, color: '#334155' }}>
            This change is recorded on {groupSize} contracts. Saving updates it on all of them.
          </Typography>
        )}
        {Object.keys(current.changes).map(renderValueField)}
        <DateField
          label="Effective on"
          ymd={current.effectiveYmd}
          minDate={dayjs(tomorrowYmd)}
          onChange={(v) => setCurrent((state) => ({ ...state, effectiveYmd: v }))}
          helperText={dateValid
            ? 'Stays pending and applies on this date'
            : 'Must be after today. To apply it now, cancel this amendment and record it with today\'s date.'}
        />
        <DateField
          label="Signed on"
          ymd={current.signedYmd}
          onChange={(v) => setCurrent((state) => ({ ...state, signedYmd: v }))}
        />
        <TextField
          label="Reason"
          value={current.reason}
          onChange={(e) => setCurrent((state) => ({ ...state, reason: e.target.value }))}
          required
          multiline
          minRows={2}
          fullWidth
        />
        <TextField
          label="Addendum reference (optional)"
          value={current.externalReference}
          onChange={(e) => setCurrent((state) => ({ ...state, externalReference: e.target.value }))}
          fullWidth
        />
        <Box sx={{ borderTop: '1px solid #E2E8F0', pt: 2 }}>
          <TextField
            label="Why are you changing it?"
            value={revisionReason}
            onChange={(e) => setRevisionReason(e.target.value)}
            helperText="Recorded in the history of this contract"
            required
            multiline
            minRows={2}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>Close</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit || isSubmitting}>
          {isSubmitting ? 'Saving…' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditAmendmentDialog;
